<template>
  <a
    :class="[{ '--active': isActive }, 'v-link']"
    :href="pagePath"
    v-on.prevent="
      withAccessChecks
        ? isPurchaseRequired
          ? { click: triggerCheckout }
          : isPresent(availableAfter)
          ? { click: triggerScheduledModal }
          : { click: handleClick }
        : { click: handleClick }
    "
  >
    <template v-if="withAccessChecks">
      <slot
        :is-loading-access="forceLock ? false : isLoadingAccess"
        :is-locked="forceLock || isLocked"
        :available-after="availableAfter"
      ></slot>
    </template>
    <template v-else>
      <slot
        :is-loading-access="false"
        :is-locked="false"
        :available-after="null"
      ></slot>
    </template>
  </a>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import EventBus, { EVENTS } from "@/lib/event-bus"
import { isPresent } from "@/lib/utils"
import { REASONS } from "@/lib/page-access-shared"
import { populatePageCache } from "@/lib/page-cache"
import { SW_UPDATE_QUERY_PARAM } from "@/lib/constants"

export default {
  props: {
    to: { type: String, default: null },
    replace: { type: Boolean, default: false },
    withAccessChecks: { type: Boolean, default: false },
    pageId: { type: [String, Number], default: null },
    parentPageId: { type: [String, Number], default: null },
    forceLock: { type: Boolean, default: false },
    isAttachment: { type: Boolean, default: false }
  },

  data() {
    return {
      isUpdateAvailable: false
    }
  },

  computed: {
    ...mapGetters("pages", [
      "hasPageAccess",
      "getPageAccess",
      "hasFetchedAccess"
    ]),
    ...mapGetters("access", [
      "isSubscribedToApp",
      "isSubscriberOnly",
      "requiresAuthentication"
    ]),
    ...mapGetters("auth", ["isAuthenticated", "hasCheckedAuthentication"]),

    pagePath() {
      return this.forceLock || this.isLoadingAccess
        ? ""
        : this.isLocked
        ? this.lockedPath
        : this.to
    },

    pageAccess() {
      return this.getPageAccess(this.pageId, this.parentPageId)
    },

    isRequirePurchaseWithNoProducts() {
      // TODOB: cypress tests
      if (isPresent(this.pageAccess) && isPresent(this.pageAccess.data)) {
        const data = this.pageAccess.data
        return (
          data.reason === REASONS.REQUIRE_PURCHASE &&
          data.product_ids.length === 0
        )
      } else {
        return false
      }
    },

    isPurchaseRequired() {
      const { data } = this.pageAccess || {}
      if (isPresent(data)) {
        return this.isAttachment
          ? data.require_purchase && !data.has_purchased
          : data.reason === REASONS.REQUIRE_PURCHASE &&
              !this.isRequirePurchaseWithNoProducts
      } else {
        return false
      }
    },

    isAuthRequired() {
      return isPresent(this.pageAccess) && isPresent(this.pageAccess.data)
        ? this.pageAccess.data.reason === REASONS.REQUIRE_AUTHENTICATION
        : false
    },

    isLoadingAccess() {
      return !this.hasFetchedAccess(this.pageId)
    },

    isLocked() {
      if (this.pageId && this.hasFetchedAccess(this.pageId)) {
        if (this.isAttachment) {
          const { data } = this.pageAccess || {}
          return (
            isPresent(data) &&
            ((data.require_purchase && !data.has_purchased) ||
              (data.require_authentication && !this.isAuthenticated))
          )
        } else {
          return this.isRequirePurchaseWithNoProducts
            ? false
            : !this.hasPageAccess(this.pageId, this.parentPageId)
        }
      } else if (this.isSubscriberOnly) {
        return !this.isSubscribedToApp
      } else if (this.requiresAuthentication) {
        return !this.isAuthenticated
      } else {
        return false
      }
    },

    lockedPath() {
      if (!this.pageAccess) return ""

      switch (this.pageAccess.data.reason) {
        case REASONS.REQUIRE_AUTHENTICATION:
          return "#auth"

        default:
          return ""
      }
    },

    availableAfter() {
      return isPresent(this.pageAccess) &&
        this.pageAccess.granted === false &&
        this.pageAccess.data.reason === REASONS.IS_SCHEDULED
        ? this.pageAccess.data.available_after
        : null
    },

    isActive() {
      return this.$route.path === this.to
    }
  },

  methods: {
    isPresent,

    ...mapActions("pages", ["addToAccessQueue"]),

    handleClick(e) {
      e.preventDefault()

      if (this.withAccessChecks) {
        if (this.forceLock) return

        if (this.isLocked) {
          if (this.isAuthRequired) {
            this.goToPath("#auth")
          }
        } else if (this.$listeners.click) {
          this.$listeners.click()
        } else {
          this.goToPath(this.to)
        }
      } else {
        if (this.$listeners.click) {
          this.$listeners.click()
        } else {
          this.goToPath(this.to)
        }
      }
    },

    goToPath(path) {
      if (this.isAttachment) {
        this.to && window.open(this.to, "_blank")
      } else if (this.isUpdateAvailable) {
        EventBus.$emit(EVENTS.SHOW_GLOBAL_LOADER)
        window.location.href = path + `?${SW_UPDATE_QUERY_PARAM}`
        setTimeout(() => {
          // This ensures the global loader is hidden even if someone on iOS
          // swipes to navigate back to the page they came from
          EventBus.$emit(EVENTS.HIDE_GLOBAL_LOADER)
        }, 1500)
      } else if (path.startsWith("http")) {
        window.open(path, "_blank")
      } else if (path === "/") {
        this.$router.goHome()
      } else if (this.replace) {
        this.$router.replace(path)
      } else {
        this.$router.push(path)
      }
    },

    triggerCheckout(e) {
      e.preventDefault()
      EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, {
        productIds: this.pageAccess.data.product_ids,
        overlay: this.pageAccess.data.purchase_overlay
      })
    },

    triggerScheduledModal(e) {
      e.preventDefault()
      EventBus.$emit(EVENTS.TRIGGER_SCHEDULED_MODAL, this.pageId)
    },

    handleSwUpdateAvailable() {
      this.isUpdateAvailable = true
    }
  },

  mounted() {
    if (this.to && this.to.startsWith("/")) populatePageCache(this.to)

    EventBus.$on(EVENTS.swUpdateAvailable, this.handleSwUpdateAvailable)
  },

  beforeDestroy() {
    EventBus.$off(EVENTS.swUpdateAvailable, this.handleSwUpdateAvailable)
  },

  watch: {
    withAccessChecks: {
      immediate: true,
      handler() {
        if (this.withAccessChecks && !isPresent(this.pageId)) {
          console.error("Property 'pageId' is required")
        }
      }
    },

    hasCheckedAuthentication: {
      immediate: true,
      handler() {
        if (
          this.hasCheckedAuthentication &&
          this.withAccessChecks &&
          !this.hasFetchedAccess(this.pageId)
        ) {
          this.addToAccessQueue(this.pageId)
        }
      }
    },

    isAuthenticated() {
      if (this.withAccessChecks) {
        this.addToAccessQueue(this.pageId)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-link {
  text-decoration: none;
}
</style>
